const SOCIAL_IMAGE_WIDTH = 1200;
const SOCIAL_IMAGE_HEIGHT = 630;

type Tag = { attributes?: Record<string, string> | null; content?: string | null; tag: string };
type PageModule = { __typename?: string } & Record<string, unknown>;
type ResponsiveImage = { src?: string | null };

// The image field each hero module type uses (see src/graphql/dato/fragments/modules/*Hero.gql)
const HERO_IMAGE_FIELDS: Record<string, string> = {
  HomeHeroV3Record: 'bottomImageDesktop',
  CurrentAccountHeroRecord: 'heroImage',
  AppHeroRecord: 'appHeroPrimaryImage',
  SecondaryProductHeroRecord: 'image',
  SwooshHeroRecord: 'swooshHeroImage',
  LoanHeroRecord: 'loanHeroImage',
  CreditCardHeroRecord: 'image',
  B2bHeroRecord: 'b2bHeroImage',
  HomeHeroRecord: 'backupImage',
  ApplyHeroRecord: 'heroImage',
  ProductHeroRecord: 'image',
  MobileHeroRecord: 'image',
  PageHeroRecord: 'image',
  SaveHeroRecord: 'image',
};

const getImageUrl = (image: unknown): string | null => {
  const { responsiveImage, url } = (image ?? {}) as { responsiveImage?: ResponsiveImage | null; url?: string | null };

  return responsiveImage?.src ?? url ?? null;
};

// Display-specific transforms that would fight with the fixed social card size
const DISPLAY_PARAMS = ['ar', 'dpr', 'max-w', 'max-h', 'min-w', 'min-h'];

// Experiment blocks hold their modules in a control and a treatment slot (the unshown one is pruned to empty)
const flattenModules = (modules: PageModule[]): PageModule[] =>
  modules.flatMap((pageModule) =>
    pageModule.__typename === 'ExperimentRecord'
      ? flattenModules([
          ...((pageModule.control as PageModule[] | undefined) ?? []),
          ...((pageModule.treatment as PageModule[] | undefined) ?? []),
        ])
      : [pageModule],
  );

/**
 * The first hero module's image (including heroes inside experiment slots), cropped to the 1200x630 social card
 * size. Dato serves images through imgix, so the size is changed with query params on the same asset. Crop and
 * focal-point params set by the CMS query are kept so the intended subject stays in frame.
 */
export const getHeroSocialImage = (modules?: PageModule[] | null): string | null => {
  for (const pageModule of flattenModules(modules ?? [])) {
    const field = pageModule.__typename ? HERO_IMAGE_FIELDS[pageModule.__typename] : undefined;
    const source = field ? getImageUrl(pageModule[field]) : null;

    if (source) {
      // A social preview isn't worth failing the page render for, so skip a relative or malformed url
      let url: URL;

      try {
        url = new URL(source);
      } catch {
        continue;
      }

      DISPLAY_PARAMS.forEach((param) => url.searchParams.delete(param));
      url.searchParams.set('w', String(SOCIAL_IMAGE_WIDTH));
      url.searchParams.set('h', String(SOCIAL_IMAGE_HEIGHT));
      url.searchParams.set('fit', 'crop');
      url.searchParams.set('auto', 'format');

      return url.toString();
    }
  }

  return null;
};

const isTag = (tag: Tag, key: 'property' | 'name', value: string) => tag.attributes?.[key] === value;

/**
 * Points the social preview tags (og:image, twitter:image) at `imageUrl`. Only used when the editor has not set
 * an SEO image on the page, in which case Dato fills the tags with the site-wide fallback image.
 */
export const withSocialImage = <T extends Tag>(tags: T[], imageUrl: string): T[] =>
  tags.map((tag) => {
    if (isTag(tag, 'property', 'og:image') || isTag(tag, 'name', 'twitter:image')) {
      return { ...tag, attributes: { ...tag.attributes, content: imageUrl } };
    }

    if (isTag(tag, 'property', 'og:image:width')) {
      return { ...tag, attributes: { ...tag.attributes, content: String(SOCIAL_IMAGE_WIDTH) } };
    }

    if (isTag(tag, 'property', 'og:image:height')) {
      return { ...tag, attributes: { ...tag.attributes, content: String(SOCIAL_IMAGE_HEIGHT) } };
    }

    return tag;
  });

/**
 * The page's meta tags with the hero image as social preview, unless an editor set an SEO image (then the tags are
 * already right) or the page has no hero image (then Dato's site-wide fallback stays).
 */
export const resolveSeoTags = <T extends Tag>(
  seo: T[],
  pageSeoImage: { url?: string | null } | undefined | null,
  modules?: PageModule[] | null,
): T[] => {
  if (pageSeoImage) {
    return seo;
  }

  const heroImage = getHeroSocialImage(modules);

  return heroImage ? withSocialImage(seo, heroImage) : seo;
};
